import React from 'react';

import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import AnalysisRunningAnimation from 'analysis-tool/ui/components/AnalysisRunningAnimation';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertPipelineStepIsStepType} from 'pipelines/PipelineTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {assertNever} from 'util/Types';

import useHandleAnalysisLayoutEvents from './analysis-adapter/useHandleAnalysisLayoutEvents';
import DataLayout from './DataLayout';

export type AnalysisDataLayoutAdapterProps = MainViewComponentProps<AnalysisMainViewItemType.DATA_LAYOUT>;

/**
 * Adapter between an analysis layout step and the actual data layout component.
 *
 * This component is responsible for handling analysis-specific concerns, such
 * as the status of the layout step.
 */
export default function AnalysisDataLayoutAdapter({
  analysis,
  clientStateId,
  step,
}: AnalysisDataLayoutAdapterProps): JSX.Element {
  const statusCode = step.statusCode;
  const layoutStep = getStepFromAnalysis(analysis, step.id);
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);

  const {handleOnEvents, layout} = useHandleAnalysisLayoutEvents({
    analysis,
    clientStateId,
    layoutStep,
  });

  // TODO(BNCH-128663, BNCH-128664): flesh out non-succeeded states more
  if (
    statusCode === PipelineStepStatusCode.RUNNING ||
    statusCode === PipelineStepStatusCode.BLOCKED ||
    statusCode === PipelineStepStatusCode.WAITING
  ) {
    return <AnalysisRunningAnimation />;
  } else if (statusCode === PipelineStepStatusCode.FAILED) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to render layout" />;
  } else if (statusCode === PipelineStepStatusCode.SUCCEEDED) {
    return (
      <DataLayout
        actionOrigin={ActionOrigin.ANALYSIS_TOOL}
        clientId={step.id}
        layout={layout}
        onEvents={handleOnEvents}
      />
    );
  } else {
    throw new Error(`Unknown status code: ${assertNever(statusCode)}`);
  }
}
